import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { qualityAPI, NonConformanceReport } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface POLineNCRTabProps {
  lineId: number;
}

const SEVERITY_STYLES: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  major: 'bg-orange-100 text-orange-700',
  minor: 'bg-yellow-100 text-yellow-700',
};

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  ca_submitted: 'bg-blue-100 text-blue-700',
  car_submitted: 'bg-indigo-100 text-indigo-700',
  closed: 'bg-green-100 text-green-700',
  deleted: 'bg-red-100 text-red-500',
};

export default function POLineNCRTab({ lineId }: POLineNCRTabProps) {
  const [ncrs, setNcrs] = useState<NonConformanceReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { formatDate: contextFormatDate } = useFormatters();

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return contextFormatDate(dateString);
  };

  useEffect(() => {
    fetchNCRs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);

  const fetchNCRs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await qualityAPI.getNCRs({ po_line: lineId, page_size: 50 });
      setNcrs(response.data.results || []);
    } catch (err: unknown) {
      console.error('Failed to fetch NCRs:', err);
      setError(getApiErrorMessage(err, 'Failed to load NCRs'));
      toast.error('Failed to load NCRs');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button onClick={fetchNCRs} className="mt-2 text-sm text-red-600 hover:text-red-800 underline">
          Try again
        </button>
      </div>
    );
  }

  if (ncrs.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">No NCRs found for this PO line</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="flex items-center gap-4">
        <div className="text-sm text-muted-foreground">
          {ncrs.length} NCR{ncrs.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* NCR Table */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="text-left px-4 py-3 font-medium">NCR Number</th>
              <th className="text-left px-4 py-3 font-medium">Date</th>
              <th className="text-left px-4 py-3 font-medium">Part Number</th>
              <th className="text-left px-4 py-3 font-medium">Severity</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Reject Qty</th>
              <th className="text-left px-4 py-3 font-medium">Raised By</th>
              <th className="text-left px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {ncrs.map((ncr) => (
              <tr key={ncr.id} className="border-b last:border-b-0 hover:bg-muted/30">
                <td className="px-4 py-3 font-medium text-primary">{ncr.ncr_number}</td>
                <td className="px-4 py-3">{formatDate(ncr.ncr_date)}</td>
                <td className="px-4 py-3">{ncr.part_number}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold capitalize ${SEVERITY_STYLES[ncr.severity] || 'bg-gray-100 text-gray-700'}`}>
                    {ncr.severity_display || ncr.severity}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATUS_STYLES[ncr.status] || 'bg-gray-100 text-gray-700'}`}>
                    {ncr.status_display || ncr.status}
                  </span>
                </td>
                <td className="px-4 py-3">{ncr.reject_qty}</td>
                <td className="px-4 py-3">{ncr.raised_by_name}</td>
                <td className="px-4 py-3">
                  <a
                    href={`/quality/ncr/${ncr.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    View <ExternalLink className="h-3 w-3" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Defect Summary */}
      {ncrs.length > 0 && (
        <div className="bg-muted/30 rounded-lg p-4">
          <h4 className="text-sm font-medium mb-2">Summary</h4>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Total NCRs</span>
              <p className="font-semibold">{ncrs.length}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Total Reject Qty</span>
              <p className="font-semibold">{ncrs.reduce((sum, n) => sum + (Number(n.reject_qty) || 0), 0)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Open</span>
              <p className="font-semibold">{ncrs.filter((n) => n.status !== 'closed' && n.status !== 'deleted').length}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Closed</span>
              <p className="font-semibold">{ncrs.filter((n) => n.status === 'closed').length}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
